// Offline-first write queue (brief §3.4). Every event the user logs goes into IndexedDB first,
// then flushes to Supabase. Each queued event carries a client_id; the ledger has a unique
// index on (user_id, client_id), so a retry after a lost response is a no-op (23505 = done).
import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { supabase } from "./supabase";
import type { NewEvent } from "./types";

interface OutboxRow {
  client_id: string;
  user_id: string;
  event: NewEvent;
  queued_at: number;
  attempts: number;
  last_error?: string;
}

interface HearthDB extends DBSchema {
  outbox: { key: string; value: OutboxRow; indexes: { by_queued: number } };
}

let dbPromise: Promise<IDBPDatabase<HearthDB>> | null = null;
function db() {
  dbPromise ??= openDB<HearthDB>("hearth", 1, {
    upgrade(d) {
      const s = d.createObjectStore("outbox", { keyPath: "client_id" });
      s.createIndex("by_queued", "queued_at");
    },
  });
  return dbPromise;
}

type Listener = (pending: number) => void;
const listeners = new Set<Listener>();
export function onOutboxChange(fn: Listener) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
async function notify() {
  const n = await pendingCount();
  listeners.forEach((l) => l(n));
}

export async function pendingCount() {
  return (await db()).count("outbox");
}

export async function enqueue(userId: string, event: NewEvent): Promise<string> {
  const client_id = crypto.randomUUID();
  await (
    await db()
  ).put("outbox", { client_id, user_id: userId, event, queued_at: Date.now(), attempts: 0 });
  void notify();
  void flush();
  return client_id;
}

let flushing = false;
export async function flush(): Promise<{ sent: number; failed: number }> {
  if (flushing) return { sent: 0, failed: 0 };
  flushing = true;
  let sent = 0;
  let failed = 0;
  try {
    const rows = await (await db()).getAllFromIndex("outbox", "by_queued");
    for (const row of rows) {
      const { error } = await supabase.from("events").insert({
        user_id: row.user_id,
        client_id: row.client_id,
        type: row.event.type,
        occurred_at: row.event.occurred_at,
        source: row.event.source ?? "manual",
        verified: row.event.verified ?? false,
        session_id: row.event.session_id ?? null,
        payload: row.event.payload,
      });
      if (!error || error.code === "23505") {
        await (await db()).delete("outbox", row.client_id);
        sent++;
      } else {
        failed++;
        await (
          await db()
        ).put("outbox", { ...row, attempts: row.attempts + 1, last_error: error.message });
        // Auth / RLS / schema errors won't fix themselves by retrying in this loop.
        if (error.code === "42501" || error.code === "PGRST301" || error.code?.startsWith("22"))
          continue;
        if (!navigator.onLine) break;
      }
    }
  } finally {
    flushing = false;
    void notify();
  }
  return { sent, failed };
}

export function startOutbox() {
  window.addEventListener("online", () => void flush());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void flush();
  });
  const t = window.setInterval(() => void flush(), 30_000);
  void flush();
  return () => window.clearInterval(t);
}
